import { useMemo, useState } from "react";
import { theme } from "../styles/theme";

const normalizarTexto = (valor) => String(valor ?? "").trim();
const normalizarCohorte = (valor) => {
  const texto = normalizarTexto(valor);
  const coincidencia = texto.match(/\d+/);
  return coincidencia ? coincidencia[0] : texto;
};

function TemariosEstudiantes({ data }) {
  const [maestriaActiva, setMaestriaActiva] = useState("todas");
  const [cohorteActiva, setCohorteActiva] = useState("todas");

  const registrosConTema = useMemo(() => {
    return data
      .filter((item) => normalizarTexto(item?.tema))
      .map((item) => ({
        ...item,
        maestriaNormalizada: normalizarTexto(item?.maestria).toUpperCase(),
        cohorteNormalizada: normalizarCohorte(item?.cohorte)
      }));
  }, [data]);

  const maestriasDisponibles = useMemo(() => {
    return Array.from(
      new Set(registrosConTema.map((item) => item.maestriaNormalizada).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, "es"));
  }, [registrosConTema]);

  const cohortesDisponibles = useMemo(() => {
    return Array.from(
      new Set(registrosConTema.map((item) => item.cohorteNormalizada).filter(Boolean))
    ).sort((a, b) => Number(a) - Number(b));
  }, [registrosConTema]);

  const registrosFiltrados = useMemo(() => {
    return registrosConTema.filter((item) => {
      const coincideMaestria =
        maestriaActiva === "todas" ? true : item.maestriaNormalizada === maestriaActiva;
      const coincideCohorte =
        cohorteActiva === "todas" ? true : item.cohorteNormalizada === cohorteActiva;

      return coincideMaestria && coincideCohorte;
    });
  }, [cohorteActiva, maestriaActiva, registrosConTema]);

  const totalRegistros = registrosFiltrados.length;

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div>
          <h2 style={styles.title}>Estudiantes con temas y temarios</h2>
          <p style={styles.subtitle}>
            Esta vista concentra a los estudiantes que ya tienen tema de tesis registrado y permite segmentarlos
            por maestria y cohorte para seguimiento academico.
          </p>
        </div>

        <div style={styles.heroBadge}>
          <span style={styles.heroLabel}>Registros visibles</span>
          <strong style={styles.heroValue}>{totalRegistros}</strong>
        </div>
      </section>

      <section style={styles.filtersPanel}>
        <div style={styles.filterBlock}>
          <span style={styles.filterLabel}>Filtrar por maestria</span>
          <div style={styles.chips}>
            <button
              type="button"
              onClick={() => setMaestriaActiva("todas")}
              style={{
                ...styles.chip,
                ...(maestriaActiva === "todas" ? styles.chipActive : {})
              }}
            >
              Todas
            </button>
            {maestriasDisponibles.map((maestria) => (
              <button
                key={maestria}
                type="button"
                onClick={() => setMaestriaActiva(maestria)}
                style={{
                  ...styles.chip,
                  ...(maestriaActiva === maestria ? styles.chipActive : {})
                }}
              >
                {maestria}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.filterBlock}>
          <span style={styles.filterLabel}>Filtrar por cohorte</span>
          <div style={styles.chips}>
            <button
              type="button"
              onClick={() => setCohorteActiva("todas")}
              style={{
                ...styles.chip,
                ...(cohorteActiva === "todas" ? styles.chipActive : {})
              }}
            >
              Todas
            </button>
            {cohortesDisponibles.map((cohorte) => (
              <button
                key={cohorte}
                type="button"
                onClick={() => setCohorteActiva(cohorte)}
                style={{
                  ...styles.chip,
                  ...(cohorteActiva === cohorte ? styles.chipActive : {})
                }}
              >
                Cohorte {cohorte}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section style={styles.tablePanel}>
        <div style={styles.tableHeader}>
          <div>
            <h3 style={styles.panelTitle}>Listado academico</h3>
            <p style={styles.panelSubtitle}>
              Se muestran estudiante, maestria, cohorte, tema y docentes principales para facilitar el seguimiento.
            </p>
          </div>
        </div>

        {registrosFiltrados.length > 0 ? (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeadRow}>
                  <th style={styles.headCell}>Estudiante</th>
                  <th style={styles.headCell}>Maestria</th>
                  <th style={styles.headCell}>Cohorte</th>
                  <th style={styles.headCell}>Estado</th>
                  <th style={styles.headCell}>Tutor</th>
                  <th style={styles.headCell}>Evaluador</th>
                  <th style={styles.headCell}>Tema o temario</th>
                </tr>
              </thead>
              <tbody>
                {registrosFiltrados.map((item, index) => (
                  <tr
                    key={`${item.cedula || item.estudiante || "registro"}-${index}`}
                    style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                  >
                    <td style={styles.cellStrong}>{item.estudiante || "Sin estudiante"}</td>
                    <td style={styles.cell}>{item.maestriaNormalizada || "Sin maestria"}</td>
                    <td style={styles.cell}>{item.cohorteNormalizada || "Sin cohorte"}</td>
                    <td style={styles.cell}>
                      <span
                        style={{
                          ...styles.stateBadge,
                          ...(normalizarTexto(item?.estado).toUpperCase() === "GRADUADO"
                            ? styles.stateBadgeGraduado
                            : styles.stateBadgePropuesta)
                        }}
                      >
                        {item.estado || "Sin estado"}
                      </span>
                    </td>
                    <td style={styles.cell}>{item.director || "Sin tutor"}</td>
                    <td style={styles.cell}>{item.evaluador || "Sin evaluador"}</td>
                    <td style={styles.topicCell}>{item.tema}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <strong style={styles.emptyTitle}>No hay estudiantes para este filtro</strong>
            <p style={styles.emptyText}>
              Cambia la maestria o la cohorte para revisar otros grupos con tema o temario registrado.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "24px"
  },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "20px",
    background: "linear-gradient(135deg, #ffffff 0%, #f7f3e8 100%)",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "20px",
    padding: "24px",
    boxShadow: "0 14px 35px rgba(18, 38, 63, 0.08)"
  },
  title: {
    margin: 0,
    color: theme.colors.text
  },
  subtitle: {
    margin: "8px 0 0",
    color: theme.colors.subtext,
    lineHeight: 1.6,
    maxWidth: "760px"
  },
  heroBadge: {
    minWidth: "180px",
    background: "#fff7e6",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "16px",
    padding: "16px"
  },
  heroLabel: {
    display: "block",
    color: theme.colors.subtext,
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: "6px"
  },
  heroValue: {
    color: theme.colors.accent,
    fontSize: "24px",
    fontWeight: 800
  },
  filtersPanel: {
    background: theme.colors.panelBg,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "18px",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    boxShadow: "0 14px 35px rgba(18, 38, 63, 0.08)"
  },
  filterBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  filterLabel: {
    color: theme.colors.subtext,
    fontSize: "14px",
    fontWeight: 700
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px"
  },
  chip: {
    background: theme.colors.panelBg,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "999px",
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700
  },
  chipActive: {
    background: theme.colors.primary,
    color: "#fff",
    border: `1px solid ${theme.colors.primary}`,
    boxShadow: "0 10px 20px rgba(19, 59, 104, 0.18)"
  },
  tablePanel: {
    background: theme.colors.panelBg,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "18px",
    padding: "22px",
    boxShadow: "0 14px 35px rgba(18, 38, 63, 0.08)"
  },
  tableHeader: {
    marginBottom: "18px"
  },
  panelTitle: {
    margin: 0,
    color: theme.colors.text
  },
  panelSubtitle: {
    margin: "8px 0 0",
    color: theme.colors.subtext,
    lineHeight: 1.5
  },
  tableWrapper: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "980px"
  },
  tableHeadRow: {
    background: theme.colors.tableHeader
  },
  headCell: {
    textAlign: "left",
    padding: "14px 16px",
    color: theme.colors.text,
    fontSize: "13px"
  },
  tableRow: {
    background: "#ffffff"
  },
  tableRowAlt: {
    background: "#f8fafc"
  },
  cell: {
    padding: "14px 16px",
    borderTop: `1px solid ${theme.colors.border}`,
    color: theme.colors.subtext,
    verticalAlign: "top"
  },
  cellStrong: {
    padding: "14px 16px",
    borderTop: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    fontWeight: 700,
    verticalAlign: "top"
  },
  topicCell: {
    padding: "14px 16px",
    borderTop: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    verticalAlign: "top",
    lineHeight: 1.5,
    minWidth: "260px"
  },
  stateBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "12px",
    border: "1px solid transparent"
  },
  stateBadgeGraduado: {
    background: "#e7f6ec",
    color: "#1d6b3b",
    borderColor: "#b4dfc2"
  },
  stateBadgePropuesta: {
    background: "#fff4dc",
    color: "#8a6116",
    borderColor: "#efd59a"
  },
  emptyState: {
    border: `1px dashed ${theme.colors.border}`,
    borderRadius: "16px",
    padding: "28px",
    background: "#fbfcfd"
  },
  emptyTitle: {
    display: "block",
    color: theme.colors.text,
    marginBottom: "8px"
  },
  emptyText: {
    margin: 0,
    color: theme.colors.subtext,
    lineHeight: 1.5
  }
};

export default TemariosEstudiantes;
